import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, MapPin, 
  Building2, Calendar, BarChart3, ArrowUp, ArrowDown,
  Loader2, RefreshCw, Download, FileText, Mail, Settings,
  Check, X
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, BarChart, Bar, Legend
} from 'recharts';
import api from '../../../utils/api';
import { toast } from '../../../hooks/use-toast';

const CommissionAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [analytics, setAnalytics] = useState(null);
  const [topRoutes, setTopRoutes] = useState([]);
  const [operatorPerformance, setOperatorPerformance] = useState([]);
  const [totals, setTotals] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [showReportSettings, setShowReportSettings] = useState(false);
  const [reportSettings, setReportSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');
  const [sendingTestReport, setSendingTestReport] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, routesRes, operatorsRes] = await Promise.all([
        api.get(`/admin/bus/commission/analytics?period=${period}`),
        api.get(`/admin/bus/commission/top-routes?period=${period}&limit=5`),
        api.get(`/admin/bus/commission/operator-performance?period=${period}`)
      ]);

      setAnalytics(analyticsRes.data);
      setTopRoutes(routesRes.data.topRoutes || []);
      setOperatorPerformance(operatorsRes.data.operators || []);
      setTotals(operatorsRes.data.totals || {});
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load analytics data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `K${value?.toLocaleString() || 0}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/bus/commission/export/csv?period=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commission_report_${period}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: 'CSV report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export CSV', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/admin/bus/commission/export/pdf?period=${period}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commission_report_${period}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: 'PDF report downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export PDF', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const fetchReportSettings = async () => {
    try {
      const response = await api.get('/admin/bus/commission/report-settings');
      setReportSettings(response.data.settings);
    } catch (error) {
      console.error('Error fetching report settings:', error);
    }
  };

  const handleSaveReportSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/bus/commission/report-settings', reportSettings);
      toast({ title: 'Success', description: 'Scheduled report settings saved' });
      setShowReportSettings(false);
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to save settings', 
        variant: 'destructive' 
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendTestReport = async () => {
    if (!reportSettings?.recipients?.length) {
      toast({ title: 'Error', description: 'Please add at least one email recipient first', variant: 'destructive' });
      return;
    }
    
    setSendingTestReport(true);
    try {
      await api.post('/admin/bus/commission/send-report-now', {
        recipients: reportSettings.recipients,
        period: period,
        include_csv: reportSettings.includeCSV !== false,
        include_pdf: reportSettings.includePDF !== false
      });
      toast({ title: 'Success', description: `Test report sent to ${reportSettings.recipients.length} recipient(s)` });
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to send test report', 
        variant: 'destructive' 
      });
    } finally {
      setSendingTestReport(false);
    }
  };

  const addRecipient = () => {
    if (newRecipient && !reportSettings.recipients.includes(newRecipient)) {
      setReportSettings({
        ...reportSettings,
        recipients: [...reportSettings.recipients, newRecipient]
      });
      setNewRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setReportSettings({
      ...reportSettings,
      recipients: reportSettings.recipients.filter(r => r !== email)
    });
  };

  const GrowthIndicator = ({ value }) => {
    const isPositive = value >= 0;
    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
        {Math.abs(value)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-gray-500">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="commission-analytics-tab">
      {/* Header with Period Selector and Export Options */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Commission Analytics</h2>
          <p className="text-sm text-gray-500">Track commission performance and trends</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['7d', '30d', '90d'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  period === p 
                    ? 'bg-white text-indigo-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid={`period-${p}`}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
          
          {/* Export Buttons */}
          <div className="flex items-center gap-1 border-l pl-2 ml-1">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportCSV}
              disabled={exporting}
              data-testid="export-csv-btn"
              title="Export as CSV"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="ml-1 hidden sm:inline">CSV</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="export-pdf-btn"
              title="Export as PDF"
            >
              <FileText className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">PDF</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                fetchReportSettings();
                setShowReportSettings(true);
              }}
              data-testid="schedule-reports-btn"
              title="Schedule automated reports"
            >
              <Mail className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline">Schedule</span>
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAllData}
            data-testid="refresh-analytics"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-indigo-600" />
            </div>
            {analytics?.summary?.commissionGrowth !== undefined && (
              <GrowthIndicator value={analytics.summary.commissionGrowth} />
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">
            {formatCurrency(analytics?.summary?.totalCommission)}
          </p>
          <p className="text-sm text-gray-500">Total Commission</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            {analytics?.summary?.bookingGrowth !== undefined && (
              <GrowthIndicator value={analytics.summary.bookingGrowth} />
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">
            {formatCurrency(analytics?.summary?.totalRevenue)}
          </p>
          <p className="text-sm text-gray-500">Total Revenue</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="p-2 bg-blue-100 rounded-lg w-fit">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">
            {analytics?.summary?.totalBookings || 0}
          </p>
          <p className="text-sm text-gray-500">Total Bookings</p>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="p-2 bg-orange-100 rounded-lg w-fit">
            <BarChart3 className="h-5 w-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-3">
            {formatCurrency(analytics?.summary?.averageCommissionPerBooking)}
          </p>
          <p className="text-sm text-gray-500">Avg. per Booking</p>
        </Card>
      </div>

      {/* Commission Trends Chart */}
      <Card className="p-6" data-testid="commission-trends-chart">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-indigo-600" />
          Commission Trends
        </h3>
        {analytics?.dailyData?.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <YAxis 
                tickFormatter={(v) => `K${v}`}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <Tooltip
                formatter={(value, name) => [formatCurrency(value), name === 'commission' ? 'Commission' : 'Revenue']}
                labelFormatter={(label) => formatDate(label)}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="commission" 
                stroke="#6366f1" 
                strokeWidth={2}
                dot={{ fill: '#6366f1', strokeWidth: 2 }}
                name="Commission"
              />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2 }}
                name="Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-500">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No booking data available for this period</p>
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Earning Routes */}
        <Card className="p-6" data-testid="top-routes-card">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-600" />
            Top Earning Routes
          </h3>
          {topRoutes.length > 0 ? (
            <div className="space-y-3">
              {topRoutes.map((route, index) => (
                <div 
                  key={route.routeId || index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`top-route-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {route.origin} → {route.destination}
                      </p>
                      <p className="text-xs text-gray-500">
                        {route.bookingCount} bookings • {route.passengerCount} passengers
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-indigo-600">{formatCurrency(route.totalCommission)}</p>
                    <p className="text-xs text-gray-500">commission</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No route data available</p>
            </div>
          )}
        </Card>

        {/* Operator Performance Comparison */}
        <Card className="p-6" data-testid="operator-performance-card">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Operator Performance
          </h3>
          {operatorPerformance.length > 0 ? (
            <div className="space-y-3">
              {operatorPerformance.map((op, index) => {
                const commissionShare = totals?.totalCommission > 0 
                  ? Math.round((op.totalCommission / totals.totalCommission) * 100) 
                  : 0;
                
                return (
                  <div 
                    key={op.operatorId}
                    className="p-3 bg-gray-50 rounded-lg"
                    data-testid={`operator-perf-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{op.companyName}</p>
                        <p className="text-xs text-gray-500">
                          {op.activeBuses} buses • {op.activeRoutes} routes • {op.commissionRate}% rate
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-indigo-600">{formatCurrency(op.totalCommission)}</p>
                        <p className="text-xs text-gray-500">{op.bookingCount} bookings</p>
                      </div>
                    </div>
                    {/* Progress bar showing share */}
                    <div className="relative pt-1">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-500">Share of total commission</span>
                        <span className="font-medium text-indigo-600">{commissionShare}%</span>
                      </div>
                      <div className="overflow-hidden h-2 text-xs flex rounded bg-indigo-100">
                        <div 
                          style={{ width: `${commissionShare}%` }}
                          className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>No operator data available</p>
            </div>
          )}
        </Card>
      </div>

      {/* Operator Comparison Bar Chart */}
      {operatorPerformance.length > 0 && (
        <Card className="p-6" data-testid="operator-comparison-chart">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            Operator Revenue & Commission Comparison
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={operatorPerformance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="companyName" 
                tick={{ fontSize: 11 }}
                stroke="#9ca3af"
                interval={0}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                tickFormatter={(v) => `K${v}`}
                tick={{ fontSize: 12 }}
                stroke="#9ca3af"
              />
              <Tooltip
                formatter={(value, name) => [formatCurrency(value), name === 'totalCommission' ? 'Commission' : 'Revenue']}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
              />
              <Legend />
              <Bar dataKey="totalRevenue" fill="#10b981" name="Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="totalCommission" fill="#6366f1" name="Commission" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Summary Footer */}
      {totals && (
        <Card className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold">{formatCurrency(totals.totalRevenue)}</p>
              <p className="text-indigo-200 text-sm">Total Platform Revenue</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(totals.totalCommission)}</p>
              <p className="text-indigo-200 text-sm">Total Platform Commission</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{totals.totalBookings}</p>
              <p className="text-indigo-200 text-sm">Total Bookings</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{totals.totalPassengers}</p>
              <p className="text-indigo-200 text-sm">Total Passengers</p>
            </div>
          </div>
        </Card>
      )}

      {/* Scheduled Reports Modal */}
      {showReportSettings && reportSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg p-6 bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Mail className="h-5 w-5 text-indigo-600" />
                Scheduled Commission Reports
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowReportSettings(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">Enable Scheduled Reports</p>
                  <p className="text-sm text-gray-500">Automatically send reports via email</p>
                </div>
                <button
                  onClick={() => setReportSettings({...reportSettings, enabled: !reportSettings.enabled})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    reportSettings.enabled ? 'bg-indigo-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    reportSettings.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {reportSettings.enabled && (
                <>
                  {/* Frequency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                    <div className="flex gap-2">
                      {['weekly', 'monthly'].map((freq) => (
                        <button
                          key={freq}
                          onClick={() => setReportSettings({...reportSettings, frequency: freq})}
                          className={`px-4 py-2 rounded-lg text-sm font-medium ${
                            reportSettings.frequency === freq 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Day Selection */}
                  {reportSettings.frequency === 'weekly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Day of Week</label>
                      <select
                        value={reportSettings.dayOfWeek}
                        onChange={(e) => setReportSettings({...reportSettings, dayOfWeek: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-lg"
                      >
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, i) => (
                          <option key={i} value={i}>{day}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {reportSettings.frequency === 'monthly' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Day of Month</label>
                      <select
                        value={reportSettings.dayOfMonth}
                        onChange={(e) => setReportSettings({...reportSettings, dayOfMonth: parseInt(e.target.value)})}
                        className="w-full p-2 border rounded-lg"
                      >
                        {[...Array(28)].map((_, i) => (
                          <option key={i+1} value={i+1}>{i+1}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Include Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Include in Report</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={reportSettings.includeCSV}
                          onChange={(e) => setReportSettings({...reportSettings, includeCSV: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">CSV Attachment</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={reportSettings.includePDF}
                          onChange={(e) => setReportSettings({...reportSettings, includePDF: e.target.checked})}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">PDF Attachment</span>
                      </label>
                    </div>
                  </div>

                  {/* Recipients */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                    <div className="flex gap-2 mb-2">
                      <Input
                        type="email"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        placeholder="email@example.com"
                        className="flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                      />
                      <Button onClick={addRecipient} size="sm">Add</Button>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {reportSettings.recipients.map((email) => (
                        <div key={email} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                          <span>{email}</span>
                          <button 
                            onClick={() => removeRecipient(email)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      {reportSettings.recipients.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-2">No recipients added</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-between gap-2 mt-6 pt-4 border-t">
              <Button 
                variant="outline"
                onClick={handleSendTestReport}
                disabled={sendingTestReport || !reportSettings?.recipients?.length}
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
              >
                {sendingTestReport ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                Send Test Report Now
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowReportSettings(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveReportSettings}
                  disabled={savingSettings}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CommissionAnalyticsTab;
